/**
 * A player's own loadout, shared with their faction (v0.87.0): read by their extension from their
 * own Items page (Torn's answer to that page — no API calls) and kept on the Omni-Torn server for
 * their faction only. Faction mates see it on that player's profile, drawn as Torn's own
 * character picture. Checked the same way on both sides: only these fields, sane values.
 */

export const LOADOUT_SLOTS = ['Primary', 'Secondary', 'Melee', 'Temporary', 'Helmet', 'Body', 'Gloves', 'Pants', 'Boots'] as const;
export type LoadoutSlot = (typeof LOADOUT_SLOTS)[number];

export interface SharedLoadoutItem {
  slot: LoadoutSlot;
  itemId: number;
  name: string;
  damage?: number | null;
  accuracy?: number | null;
  armor?: number | null;
  bonuses?: string[];
  rarity?: 'yellow' | 'orange' | 'red';
  layers?: { map: string[]; changed: string[] };
}

export interface SharedLoadout {
  items: SharedLoadoutItem[];
  gender?: 'm' | 'f';
}

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const stat = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) && x >= 0 && x < 10_000 ? Math.round(x * 100) / 100 : null);
const codes = (x: unknown): string[] => (Array.isArray(x) ? x.filter((c): c is string => typeof c === 'string' && /^[A-Z]\d{1,2}$/.test(c)).slice(0, 8) : []);

/** Only the known fields with sane values; null when nothing usable is left. */
export function cleanSharedLoadout(x: unknown): SharedLoadout | null {
  const o = obj(x);
  if (!o || !Array.isArray(o.items)) return null;
  const items: SharedLoadoutItem[] = [];
  const seen = new Set<string>();
  for (const raw of o.items.slice(0, 12)) {
    const i = obj(raw);
    const slot = i?.slot as LoadoutSlot;
    const itemId = i?.itemId;
    const name = typeof i?.name === 'string' ? i.name.trim().slice(0, 60) : '';
    if (!i || !(LOADOUT_SLOTS as readonly string[]).includes(slot) || !Number.isInteger(itemId) || (itemId as number) <= 0 || (itemId as number) > 1e6 || !name || seen.has(slot)) continue;
    seen.add(slot);
    const out: SharedLoadoutItem = { slot, itemId: itemId as number, name };
    const damage = stat(i.damage);
    const accuracy = stat(i.accuracy);
    const armor = stat(i.armor);
    if (damage !== null) out.damage = damage;
    if (accuracy !== null) out.accuracy = accuracy;
    if (armor !== null) out.armor = armor;
    const bonuses = Array.isArray(i.bonuses) ? i.bonuses.filter((b): b is string => typeof b === 'string').map((b) => b.slice(0, 60)).slice(0, 4) : [];
    if (bonuses.length) out.bonuses = bonuses;
    if (i.rarity === 'yellow' || i.rarity === 'orange' || i.rarity === 'red') out.rarity = i.rarity;
    const l = obj(i.layers);
    const map = codes(l?.map);
    const changed = codes(l?.changed);
    if (map.length || changed.length) out.layers = { map, changed };
    items.push(out);
  }
  if (!items.length) return null;
  return { items, ...(o.gender === 'm' || o.gender === 'f' ? { gender: o.gender } : {}) };
}

// ---------------------------------------------------------------- from the API (v0.93.0)

const WEAPON_SLOTS: readonly LoadoutSlot[] = ['Primary', 'Secondary', 'Melee', 'Temporary'];
/** Armor's slot by its name (Torn's armor names say what they are: "Riot Helmet", "Vanguard Respirator", "Combat Pants"…). */
const ARMOR_BY_NAME: ReadonlyArray<[RegExp, LoadoutSlot]> = [
  [/\b(gloves?|gauntlets?|mittens?)\b/i, 'Gloves'],
  [/\b(boots?|shoes?|sneakers?|sandals?)\b/i, 'Boots'],
  [/\b(pants|trousers|leggings|chaps|shorts)\b/i, 'Pants'],
  [/\b(helmet|hat|mask|respirator|visor|hood|cap|headgear|goggles|balaclava|headdress)\b/i, 'Helmet'],
  [/\b(body|vest|jacket|breastplate|apron|coat|armou?r|suit|plate)\b/i, 'Body'],
];

/**
 * A player's equipped weapons and armor from Torn's `equipment` answer (v1 `{ equipment: [{ ID, name, type, equipped }] }`
 * or v2 `{ equipment: [{ id, name, type, sub_type, slot, stats, bonuses }] }`) — for "Your faction" members who share
 * their loadout without the extension. Lenient: only items it can place in a slot for sure (weapons by their type,
 * armor by its name); stats and bonuses when the answer has them. Null when nothing usable.
 */
export function loadoutFromEquipment(body: unknown): SharedLoadout | null {
  const b = obj(body);
  const raw = b?.equipment;
  const list = Array.isArray(raw) ? raw : obj(raw) ? Object.values(raw as object) : [];
  const items: SharedLoadoutItem[] = [];
  for (const x of list) {
    const i = obj(x);
    if (!i) continue;
    const equipped = i.equipped ?? i.slot ?? i.equipped_slot;
    if (equipped === false || equipped === 0 || equipped === '0') continue;
    const itemId = Number(i.ID ?? i.id ?? i.item_id);
    const name = typeof i.name === 'string' ? i.name : '';
    const types = [i.sub_type, i.type, i.category].filter((t): t is string => typeof t === 'string');
    let slot: LoadoutSlot | null = (WEAPON_SLOTS.find((s) => types.some((t) => t.toLowerCase() === s.toLowerCase())) as LoadoutSlot | undefined) ?? null;
    if (!slot && types.some((t) => /^(defensive|armou?r)$/i.test(t))) slot = ARMOR_BY_NAME.find(([re]) => re.test(name))?.[1] ?? null;
    if (!slot) continue;
    const st = obj(i.stats);
    const bonuses = Array.isArray(i.bonuses) ? i.bonuses.map((x) => (typeof x === 'string' ? x : [obj(x)?.title ?? obj(x)?.bonus, obj(x)?.value].filter((v) => v !== undefined && v !== null).join(' '))).filter(Boolean) : undefined;
    items.push({ slot, itemId, name, damage: (st?.damage ?? i.damage) as number, accuracy: (st?.accuracy ?? i.accuracy) as number, armor: (st?.armor ?? i.armor) as number, ...(bonuses?.length ? { bonuses } : {}), rarity: (typeof i.rarity === 'string' ? i.rarity.toLowerCase() : undefined) as SharedLoadoutItem['rarity'] });
  }
  return cleanSharedLoadout({ items });
}
